import { Box } from '@mui/system'
import { Trans } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { Dropdown } from '../../components/atoms/Dropdown'
import { Port } from '../../types/models'
import { PortConfigTemperatureNTCParams, PortConfigTemperatureOherPortParams, TemperatureConfig, TemperaturePort } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { Text } from '../../components/atoms/Text'
import { Autocomplete } from '../../components/atoms/Autocomplete'
import { Stack } from '@mui/material'
import { ApplianceSubtype } from '../appliances/Appliance'

/**
 * Controller for temperature meter.
 */
export class TemperaturePortControl extends PortControl<TemperaturePort, TemperatureConfig> {

  port: TemperaturePort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as TemperaturePort
  }

  getDescription(): string { return this.app.t('Temperature measuring') }

  getTypeName(): string { return this.app.t('PortType-Temperature') }

  getName(): string {
    const slot = parseInt(this.port.port_number.split('/')[1])
    if (slot) {
      return this.app.t('Temperature Slot {slot}').replace('{slot}', `${slot}`)
    }
    return this.app.t('CPU Temperature')
  }

  get group(): string { return this.app.t('PortGroup-Info') }

  getSubtype(): ApplianceSubtype {
    return this.getConfig('driver')
  }

  getConfigDefaults(): TemperatureConfig {
    switch (this.getConfig('driver')) {
      case 'TEMPERATURE_DRIVER_DS18B20':
        return {
          enabled: false,
          driver: 'TEMPERATURE_DRIVER_DS18B20',
          params: {
            instance: 2,
            port: 2
          }
        }
      case 'TEMPERATURE_DRIVER_NTC':
        return {
          enabled: false,
          driver: 'TEMPERATURE_DRIVER_NTC',
          params: {
            r_at_25: 10000,
            b_value: 4000,
            calib_lut: [0, 0, 0, 0]
          }
        }
      case 'TEMPERATURE_DRIVER_OTHERPORT':
        return {
          enabled: false,
          driver: 'TEMPERATURE_DRIVER_OTHERPORT',
          params: {
            instance: 0,
            port: 0,
            id: 0,
          }
        }
      default:
        return {
          enabled: false,
          driver: 'TEMPERATURE_DRIVER_CPU',
          params: {
            calib_offset: 0,
            disable_dynamic_compensation: false
          }
        }
    }
  }

  onChangeParam(name: string, value: string | number): void {
    this.setConfig('params', { ...this.getConfig('params'), [name]: value })
  }

  infoTop(): React.ReactNode | undefined {
    const temperature = this.getValue<number | null>('temperature')
    const error = this.getValue<boolean | undefined>('error')

    return <Box>
      <Trans>Measuring Data</Trans>
      <Box data-cy="box-Temperature" sx={{ mt: 2 }}>
        <Trans>Temperature</Trans>: {error || temperature === undefined || temperature === null ? <>—</> : `${sprintf('%.1f', temperature)} °C`}
      </Box>
    </Box>
  }

  configTopLeft() : React.ReactNode | undefined {
    const onChangePort = (e: string) => {
      switch (e) {
        case '1':
          this.setConfig('params', {
            port: 2,
            instance: 1
          })
          break

        case '2':
          this.setConfig('params', {
            port: 2,
            instance: 2
          })
          break

        case '3':
          this.setConfig('params', {
            port: 2,
            instance: 3
          })
          break
      }
    }

    const driver = this.getConfig('driver')

    if (driver === 'TEMPERATURE_DRIVER_CPU') {
      return <Box>
        <Trans>This is internal CPU temperature.</Trans>
      </Box>
    }

    if (driver === 'TEMPERATURE_DRIVER_DS18B20') {
      return <Box>
        <Trans>Settings</Trans>
        <Dropdown
          sx={{ mt: 2 }}
          label={this.app.t('Physical Port')}
          items={{
            1: '21', 2: '22', 3: '23'
          }}
          selected={(this.getConfig('params') as {instance: number}).instance + ''}
          onSelect={value => onChangePort(value)}
        />
      </Box>
    }

    if (driver === 'TEMPERATURE_DRIVER_NTC') {
      const params: PortConfigTemperatureNTCParams = this.getConfig('params')

      return <Box>
        <Trans>Settings</Trans>
        <Stack>
          <Text
            margin="normal"
            label={this.app.t('β-value')}
            sx={{ width: '50%' }}
            size="small"
            value={params.b_value}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => this.onChangeParam('b_value', e.target.value === '' ? '' : parseInt(e.target.value))}
          />
          <Autocomplete
            sx={{ width: '50%' }}
            label={this.app.t('R₂₅ kΩ')}
            size="small"
            freeSolo
            options={['4.7', '10']}
            value={params.r_at_25 + '' === '' ? '' : '' + (params.r_at_25 / 1000)}
            onBlur={(e) => {
              if ((e.target as HTMLInputElement).value) {
                this.onChangeParam('r_at_25', parseFloat((e.target as HTMLInputElement).value) * 1000)
              } else {
                this.onChangeParam('r_at_25', 0)
              }
            }}
          />
        </Stack>
      </Box>
    }

    if (driver === 'TEMPERATURE_DRIVER_OTHERPORT') {
      return <Box>
        <Trans>Settings</Trans>
        <Stack>
          <Text
            margin="normal"
            label={this.app.t('Port')}
            sx={{ width: '30%', ml: 2, mt: 3 }}
            size="small"
            value={(this.getConfig('params') as PortConfigTemperatureOherPortParams).port}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => this.onChangeParam('port', e.target.value === '' ? '' : parseInt(e.target.value))}
          />
          <Text
            margin="normal"
            label={this.app.t('Instance')}
            sx={{ width: '30%', ml: 2 }}
            size="small"
            value={(this.getConfig('params') as PortConfigTemperatureOherPortParams).instance}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => this.onChangeParam('instance', e.target.value === '' ? '' : parseInt(e.target.value))}
          />
          <Text
            margin="normal"
            label={this.app.t('Sensor Number')}
            sx={{ width: '50%', ml: 2 }}
            size="small"
            value={(this.getConfig('params') as PortConfigTemperatureOherPortParams).id}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => this.onChangeParam('id', e.target.value === '' ? '' : parseInt(e.target.value))}
          />
        </Stack>
      </Box>
    }

    // No settings for a missing or unknown driver. This runs on every redraw, so it must
    // not throw: a port that has never been configured has no driver of its own, only
    // the pending one, and that is gone as soon as the wizard is cancelled.
    return undefined
  }

  configTopRight() : React.ReactNode | undefined {

    const driver = this.getConfig('driver')

    if (driver === 'TEMPERATURE_DRIVER_NTC') {

      const params: PortConfigTemperatureNTCParams = this.getConfig('params')

      const onChangeParam = (idx: number, value: string): void => {
        const lut: (string | number)[] = params.calib_lut
        lut[idx] = value
        this.setConfig('params', { ...this.getConfig('params'), calib_lut: lut })
      }

      const onBlur = (idx: number) => {
        const lut: (string | number)[] = params.calib_lut
        try {
          lut[idx] = lut[idx] === '' ? 0 : parseInt(lut[idx] + '')
        } catch (e) {
          lut[idx] = 0
        }
        this.setConfig('params', { ...this.getConfig('params'), calib_lut: lut })
      }

      const values = [params.calib_lut[0], params.calib_lut[1], params.calib_lut[2], params.calib_lut[3]]

      return (<>
        <Trans>Fine tune</Trans>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: '1rem'
        }}>
          <Text
            margin="normal"
            label={this.app.t('-20°C')}
            size="small"
            value={values[0]}
            type="text"
            onChange={(e) => onChangeParam(0, e.target.value)}
            onBlur={(e) => onBlur(0)}
          />
          <Text
            margin="normal"
            label={this.app.t('+20°C')}
            size="small"
            value={values[1]}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => onChangeParam(1, e.target.value)}
            onBlur={(e) => onBlur(1)}
          />
          <Text
            margin="normal"
            label={this.app.t('+60 °C')}
            size="small"
            value={values[2]}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => onChangeParam(2, e.target.value)}
            onBlur={(e) => onBlur(2)}
          />
          <Text
            margin="normal"
            label={this.app.t('+100°C')}
            size="small"
            value={values[3]}
            inputProps={{ inputMode: 'numeric' }}
            type="text"
            onChange={(e) => onChangeParam(3, e.target.value)}
            onBlur={(e) => onBlur(3)}
          />
        </Box>
      </>)

    }
    return <></>
  }
}
